import { motion } from "framer-motion";
import { Plus, ArrowUpRight } from "lucide-react";
import { DOMAINS } from "@/data/content";
import { useLanguage } from "@/i18n/LanguageContext";

const SPANS = [
  "md:col-span-7",
  "md:col-span-5",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-4",
  "md:col-span-12",
];

function DomainCard({ domain, meta, span, index }) {
  return (
    <motion.article
      data-testid={`expertise-card-${meta.id}`}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
      className={`group col-span-1 ${span} bg-white border border-slate-200 hover:border-[#E31E24]/50 flex flex-col transition-colors duration-300 ${
        span.includes("col-span-12") ? "md:flex-row" : ""
      }`}
    >
      <div
        className={`relative overflow-hidden ${
          span.includes("col-span-12") ? "md:w-2/5 aspect-[16/10] md:aspect-auto" : "aspect-[16/10]"
        }`}
      >
        <img
          src={meta.image}
          alt={domain.title}
          loading="lazy"
          className="w-full h-full object-cover grayscale-[60%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 to-transparent" />
        <span className="absolute top-4 left-4 font-mono-tech text-[11px] tracking-[0.25em] text-white bg-slate-950/60 backdrop-blur-sm px-3 py-1.5 border border-white/15">
          {meta.code}
        </span>
      </div>

      <div className="flex-1 p-6 sm:p-8 flex flex-col">
        <div className="flex items-start justify-between gap-4">
          <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-tight text-slate-900">
            {domain.title}
          </h3>
          <ArrowUpRight
            size={22}
            className="shrink-0 text-slate-300 group-hover:text-[#E31E24] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300"
          />
        </div>
        <p className="mt-3 text-sm sm:text-base text-slate-600 leading-relaxed">
          {domain.description}
        </p>
        <ul className="mt-6 pt-6 border-t border-slate-100 space-y-4">
          {domain.services.map((s) => (
            <li key={s.label} className="flex gap-3">
              <Plus size={15} strokeWidth={3} className="mt-1 shrink-0 text-[#E31E24]" />
              <div>
                <div className="text-sm font-semibold text-slate-900">{s.label}</div>
                <div className="text-sm text-slate-500 leading-relaxed">{s.detail}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </motion.article>
  );
}

export default function Expertise() {
  const { t } = useLanguage();
  return (
    <section
      id="expertises"
      data-testid="expertise-section"
      className="py-20 lg:py-32 bg-slate-50 blueprint-grid-light"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14"
        >
          <div>
            <div className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#E31E24] font-semibold mb-4">
              {t.expertise.overline}
            </div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-5xl font-bold tracking-tight text-slate-900 max-w-2xl">
              {t.expertise.title}
            </h2>
          </div>
          <p className="text-base sm:text-lg text-slate-600 max-w-md lg:text-right">
            {t.expertise.text}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="expertise-grid">
          {t.expertise.domains.map((d, i) => (
            <DomainCard key={DOMAINS[i].id} domain={d} meta={DOMAINS[i]} span={SPANS[i]} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
